import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Settings,
  Bell,
  MapPin,
  Smartphone,
  Shield,
  Server,
  Webhook,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Save,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  useFleetGpsSettings, 
  useSaveFleetGpsSettings, 
  useTestTraccarConnection 
} from '@/hooks/useFleetGpsSettings';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface GpsSettingsContentProps {
  companyId: string;
}

export function GpsSettingsContent({ companyId }: GpsSettingsContentProps) {
  // Hooks para Traccar
  const { data: gpsSettings, isLoading: traccarLoading } = useFleetGpsSettings(companyId);
  const saveMutation = useSaveFleetGpsSettings(companyId);
  const testConnectionMutation = useTestTraccarConnection();
  
  // Estado do formulário Traccar
  const [traccarForm, setTraccarForm] = useState({
    serverUrl: '',
    username: '',
    password: '',
  });
  
  // Estado das configurações gerais GPS
  const [settings, setSettings] = useState({
    // General Settings
    live_refresh_interval: 30,
    map_provider: 'openstreetmap',
    default_zoom: 12,
    cluster_devices: true,
    show_vehicle_names: true,
    
    // Alert Settings
    overspeed_default: 80,
    idle_timeout_minutes: 30,
    offline_timeout_minutes: 5,
    alert_sound_enabled: true,
    
    // Notification Settings
    webhook_url: '',
    webhook_enabled: false,
    email_notifications: true,
    telegram_bot_token: '',
    telegram_chat_id: '',
    
    // Data Retention
    retention_months: 12,
    auto_cleanup_enabled: true,
    export_format: 'csv',
    
    // Device Management
    auto_register_devices: false,
    require_device_approval: true,
    device_timeout_hours: 24,
    
    // Public Sharing
    public_sharing_enabled: false,
    public_link_expiry_hours: 24,
    require_auth_for_sharing: true
  });

  const [isLoading, setIsLoading] = useState(false);

  // Sincronizar traccarForm com dados carregados
  useEffect(() => {
    if (gpsSettings?.traccar) {
      setTraccarForm({
        serverUrl: gpsSettings.traccar.serverUrl || '',
        username: gpsSettings.traccar.username || '',
        password: '',
      });
    }
  }, [gpsSettings]);

  // Testar conexão Traccar
  const handleTestConnection = async () => {
    if (!traccarForm.serverUrl || !traccarForm.username) {
      toast.error('Preencha URL e usuário para testar conexão');
      return;
    }

    let cleanUrl = traccarForm.serverUrl.trim();
    if (cleanUrl.endsWith('/api/session')) {
      cleanUrl = cleanUrl.replace(/\/api\/session\/?$/, '');
    }
    cleanUrl = cleanUrl.replace(/\/$/, '');

    const passwordToUse = traccarForm.password || gpsSettings?.traccar?.password;
    if (!passwordToUse) {
      toast.error('Senha é obrigatória para testar conexão');
      return;
    }

    await testConnectionMutation.mutateAsync({
      serverUrl: cleanUrl,
      username: traccarForm.username,
      password: passwordToUse,
    });
  };

  const handleTestWebhook = async () => {
    if (!settings.webhook_url) {
      toast.error('Digite uma URL de webhook válida');
      return;
    }
    
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Webhook testado com sucesso!');
    } catch (error) {
      toast.error('Erro ao testar webhook');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Tabs defaultValue="server" className="space-y-4">
      <TabsList className="grid w-full grid-cols-6">
        <TabsTrigger value="server">
          <Server className="h-4 w-4 mr-2" />
          Servidor
        </TabsTrigger>
        <TabsTrigger value="general">
          <Settings className="h-4 w-4 mr-2" />
          Mapa
        </TabsTrigger>
        <TabsTrigger value="alerts">
          <Bell className="h-4 w-4 mr-2" />
          Alertas
        </TabsTrigger>
        <TabsTrigger value="notifications">
          <Webhook className="h-4 w-4 mr-2" />
          Notificações
        </TabsTrigger>
        <TabsTrigger value="devices">
          <Smartphone className="h-4 w-4 mr-2" />
          Dispositivos
        </TabsTrigger>
        <TabsTrigger value="data">
          <Shield className="h-4 w-4 mr-2" />
          Retenção
        </TabsTrigger>
      </TabsList>

      {/* Aba Servidor Traccar */}
      <TabsContent value="server" className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Configure a conexão com o servidor Traccar para integração GPS.
            As credenciais são criptografadas e armazenadas com segurança.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Configuração do Servidor Traccar
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Configure a conexão com o servidor Traccar para rastreamento GPS
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="traccar-url">URL do Servidor Traccar *</Label>
              <Input
                id="traccar-url"
                type="url"
                placeholder="http://seu-servidor.com:8082"
                value={traccarForm.serverUrl}
                onChange={(e) => setTraccarForm({ ...traccarForm, serverUrl: e.target.value })}
                disabled={traccarLoading || saveMutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                URL base do servidor Traccar (ex: http://traccar.exemplo.com:8082)
                <br />
                <span className="text-amber-600 dark:text-amber-400">
                  ⚠️ Não inclua "/api/session" no final
                </span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="traccar-username">Usuário Admin *</Label>
              <Input
                id="traccar-username"
                type="text"
                placeholder="admin"
                value={traccarForm.username}
                onChange={(e) => setTraccarForm({ ...traccarForm, username: e.target.value })}
                disabled={traccarLoading || saveMutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                Usuário com permissões administrativas no Traccar
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="traccar-password">
                Senha Admin *
                {gpsSettings?.traccar?.password && (
                  <span className="text-xs text-green-600 dark:text-green-400 ml-2">
                    ✓ Senha salva anteriormente
                  </span>
                )}
              </Label>
              <Input
                id="traccar-password"
                type="password"
                placeholder={
                  gpsSettings?.traccar?.password 
                    ? "Deixe em branco para manter a senha atual" 
                    : "Digite a senha do administrador"
                }
                value={traccarForm.password}
                onChange={(e) => setTraccarForm({ ...traccarForm, password: e.target.value })}
                disabled={traccarLoading || saveMutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                {gpsSettings?.traccar?.password 
                  ? "A senha está salva. Digite apenas se quiser alterá-la." 
                  : "Senha do usuário administrador do Traccar"}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => saveMutation.mutate({
                  serverUrl: traccarForm.serverUrl,
                  username: traccarForm.username,
                  password: traccarForm.password || gpsSettings?.traccar?.password || undefined,
                  enabled: true,
                })}
                disabled={
                  !traccarForm.serverUrl || 
                  !traccarForm.username || 
                  saveMutation.isPending ||
                  traccarLoading
                }
              >
                <Save className="mr-2 h-4 w-4" />
                {saveMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
              </Button>

              <Button
                variant="outline"
                onClick={handleTestConnection}
                disabled={
                  !traccarForm.serverUrl || 
                  !traccarForm.username || 
                  (!traccarForm.password && !gpsSettings?.traccar?.password) ||
                  testConnectionMutation.isPending ||
                  traccarLoading
                }
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${testConnectionMutation.isPending ? 'animate-spin' : ''}`} />
                {testConnectionMutation.isPending ? 'Testando...' : 'Testar Conexão'}
              </Button>
            </div>

            {testConnectionMutation.isSuccess && testConnectionMutation.data?.success && (
              <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  Conexão estabelecida com sucesso! Usuário: {testConnectionMutation.data.user}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Como Obter as Credenciais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div>
              <strong>1. Acesse seu servidor Traccar</strong>
              <p>Faça login no painel administrativo do Traccar</p>
            </div>
            <div>
              <strong>2. URL do Servidor</strong>
              <p>Use a URL completa do seu servidor (ex: https://traccar.seudominio.com)</p>
            </div>
            <div>
              <strong>3. Credenciais de Admin</strong>
              <p>Use o usuário e senha de uma conta com permissões administrativas</p>
            </div>
            <div>
              <strong>⚠️ Segurança</strong>
              <p>As credenciais são armazenadas de forma segura e usadas apenas para criar dispositivos automaticamente</p>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Aba Mapa/General */}
      <TabsContent value="general" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Configurações do Mapa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="refresh-interval">Intervalo de Atualização (segundos)</Label>
                <Input
                  id="refresh-interval"
                  type="number"
                  value={settings.live_refresh_interval}
                  onChange={(e) => setSettings({
                    ...settings,
                    live_refresh_interval: parseInt(e.target.value)
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="map-provider">Provedor do Mapa</Label>
                <Select 
                  value={settings.map_provider} 
                  onValueChange={(value) => setSettings({
                    ...settings, 
                    map_provider: value
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openstreetmap">OpenStreetMap</SelectItem>
                    <SelectItem value="google">Google Maps</SelectItem>
                    <SelectItem value="mapbox">Mapbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="default-zoom">Zoom Padrão</Label>
                <Input
                  id="default-zoom"
                  type="number"
                  min="1"
                  max="20"
                  value={settings.default_zoom}
                  onChange={(e) => setSettings({
                    ...settings,
                    default_zoom: parseInt(e.target.value)
                  })}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Agrupar Dispositivos Próximos</Label>
                  <p className="text-sm text-muted-foreground">
                    Agrupa dispositivos próximos em clusters no mapa
                  </p>
                </div>
                <Switch
                  checked={settings.cluster_devices}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    cluster_devices: checked
                  })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Mostrar Nomes dos Veículos</Label>
                  <p className="text-sm text-muted-foreground">
                    Exibe nomes dos veículos nos marcadores do mapa
                  </p>
                </div>
                <Switch
                  checked={settings.show_vehicle_names}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    show_vehicle_names: checked
                  })}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Aba Alertas */}
      <TabsContent value="alerts" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Configurações de Alerta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="overspeed">Limite de Velocidade Padrão (km/h)</Label>
                <Input
                  id="overspeed"
                  type="number"
                  value={settings.overspeed_default}
                  onChange={(e) => setSettings({
                    ...settings,
                    overspeed_default: parseInt(e.target.value)
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="idle-timeout">Timeout de Ociosidade (minutos)</Label>
                <Input
                  id="idle-timeout"
                  type="number"
                  value={settings.idle_timeout_minutes}
                  onChange={(e) => setSettings({
                    ...settings,
                    idle_timeout_minutes: parseInt(e.target.value)
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="offline-timeout">Timeout Offline (minutos)</Label>
                <Input
                  id="offline-timeout"
                  type="number"
                  value={settings.offline_timeout_minutes}
                  onChange={(e) => setSettings({
                    ...settings,
                    offline_timeout_minutes: parseInt(e.target.value)
                  })}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Som de Alerta</Label>
                <p className="text-sm text-muted-foreground">
                  Reproduz som quando novos alertas são gerados
                </p>
              </div>
              <Switch
                checked={settings.alert_sound_enabled}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  alert_sound_enabled: checked
                })}
              />
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Aba Notificações */}
      <TabsContent value="notifications" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Webhook className="h-5 w-5" />
              Webhooks
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Habilitar Webhooks</Label>
                <p className="text-sm text-muted-foreground">
                  Envie notificações para sistemas externos
                </p>
              </div>
              <Switch
                checked={settings.webhook_enabled}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  webhook_enabled: checked
                })}
              />
            </div>
            
            {settings.webhook_enabled && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="webhook-url">URL do Webhook</Label>
                  <div className="flex gap-2">
                    <Input
                      id="webhook-url"
                      placeholder="https://seu-sistema.com/webhook"
                      value={settings.webhook_url}
                      onChange={(e) => setSettings({
                        ...settings,
                        webhook_url: e.target.value
                      })}
                    />
                    <Button variant="outline" onClick={handleTestWebhook}>
                      Testar
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground p-3 bg-muted rounded">
                  <p><strong>Payload de exemplo:</strong></p>
                  <pre className="mt-2 overflow-x-auto">{`{
  "event": "overspeed",
  "device": "ABC-1234",
  "timestamp": "2025-09-28T10:30:00Z",
  "data": {
    "speed": 95,
    "limit": 80,
    "location": "lat,lng"
  }
}`}</pre>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Telegram</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telegram-token">Bot Token</Label>
                <Input
                  id="telegram-token"
                  placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyZ"
                  value={settings.telegram_bot_token}
                  onChange={(e) => setSettings({
                    ...settings,
                    telegram_bot_token: e.target.value
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telegram-chat">Chat ID</Label>
                <Input
                  id="telegram-chat"
                  placeholder="-1001234567890"
                  value={settings.telegram_chat_id}
                  onChange={(e) => setSettings({
                    ...settings,
                    telegram_chat_id: e.target.value
                  })}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Aba Dispositivos */}
      <TabsContent value="devices" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Gerenciamento de Dispositivos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Auto-registro de Dispositivos</Label>
                  <p className="text-sm text-muted-foreground">
                    Registra automaticamente novos dispositivos que enviam dados
                  </p>
                </div>
                <Switch
                  checked={settings.auto_register_devices}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    auto_register_devices: checked
                  })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Exigir Aprovação de Dispositivos</Label>
                  <p className="text-sm text-muted-foreground">
                    Novos dispositivos precisam ser aprovados manualmente
                  </p>
                </div>
                <Switch
                  checked={settings.require_device_approval}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    require_device_approval: checked
                  })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="device-timeout">Timeout de Dispositivo (horas)</Label>
                <Input
                  id="device-timeout"
                  type="number"
                  value={settings.device_timeout_hours}
                  onChange={(e) => setSettings({
                    ...settings,
                    device_timeout_hours: parseInt(e.target.value)
                  })}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Aba Retenção de Dados */}
      <TabsContent value="data" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Retenção de Dados
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="retention">Período de Retenção (meses)</Label>
                <Input
                  id="retention"
                  type="number"
                  value={settings.retention_months}
                  onChange={(e) => setSettings({
                    ...settings,
                    retention_months: parseInt(e.target.value)
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="export-format">Formato de Exportação</Label>
                <Select 
                  value={settings.export_format} 
                  onValueChange={(value) => setSettings({
                    ...settings, 
                    export_format: value
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="excel">Excel</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="gpx">GPX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Limpeza Automática</Label>
                <p className="text-sm text-muted-foreground">
                  Remove automaticamente dados antigos conforme política de retenção
                </p>
              </div>
              <Switch
                checked={settings.auto_cleanup_enabled}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  auto_cleanup_enabled: checked
                })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5" />
              Compartilhamento Público
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Permitir Links Públicos</Label>
                <p className="text-sm text-muted-foreground">
                  Permite criação de links públicos para viagens
                </p>
              </div>
              <Switch
                checked={settings.public_sharing_enabled}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  public_sharing_enabled: checked
                })}
              />
            </div>
            
            {settings.public_sharing_enabled && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="link-expiry">Expiração do Link (horas)</Label>
                  <Input
                    id="link-expiry"
                    type="number"
                    value={settings.public_link_expiry_hours}
                    onChange={(e) => setSettings({
                      ...settings,
                      public_link_expiry_hours: parseInt(e.target.value)
                    })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Exigir Autenticação</Label>
                    <p className="text-sm text-muted-foreground">
                      Usuários precisam fazer login para acessar links compartilhados
                    </p>
                  </div>
                  <Switch
                    checked={settings.require_auth_for_sharing}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      require_auth_for_sharing: checked
                    })}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
